import React, { useEffect, useState, Fragment } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { NavBar } from '../components/NavBar';
import { Card } from '../components/SubscriptionAppCard';
import { Badge } from '../components/SubscriptionAppBadge';
import { Button } from '../components/SubscriptionAppButton';
import { RefreshCw, Package, ArrowRight, X, ChevronDown, ChevronUp, Info, AlertTriangle, CheckCircle, Search, Wine, ArrowRightLeft, Lightbulb } from 'lucide-react';
// Mock product data
const mockProducts = [{
  id: '1',
  name: 'Acme Shiraz 2022',
  sku: 'SHIRAZ-2022-750',
  price: 45.0,
  shopifyPrice: 45.0,
  stock: 12,
  image: '🍷',
  subscriptionCount: 23
}, {
  id: '2',
  name: 'Acme Shiraz 2023',
  sku: 'SHIRAZ-2023-750',
  price: 48.0,
  shopifyPrice: 48.0,
  stock: 156,
  image: '🍷',
  subscriptionCount: 12,
  hasPriceMismatch: true,
  subscriptionPrice: 45.0
}, {
  id: '3',
  name: 'Acme Chardonnay 2021',
  sku: 'CHARD-2021-750',
  price: 38.0,
  shopifyPrice: 40.0,
  stock: 45,
  image: '🍷',
  subscriptionCount: 15,
  hasPriceMismatch: true,
  subscriptionPrice: 38.0
}, {
  id: '4',
  name: 'Acme Chardonnay 2022',
  sku: 'CHARD-2022-750',
  price: 40.0,
  shopifyPrice: 42.0,
  stock: 89,
  image: '🍷',
  subscriptionCount: 8,
  hasPriceMismatch: true,
  subscriptionPrice: 40.0
}, {
  id: '5',
  name: '2023 Reserve Cabernet',
  sku: '23RCAB-750',
  price: 42.0,
  shopifyPrice: 42.0,
  stock: 45,
  image: '🍷',
  subscriptionCount: 89
}, {
  id: '6',
  name: '2024 Sparkling Brut',
  sku: '24SPB-750',
  price: 35.0,
  shopifyPrice: 35.0,
  stock: 0,
  image: '🥂',
  subscriptionCount: 34
}, {
  id: '7',
  name: '2023 Rosé',
  sku: '23RSE-750',
  price: 32.0,
  shopifyPrice: 32.0,
  stock: 15,
  image: '🍷',
  subscriptionCount: 42
}, {
  id: '8',
  name: '2025 Chardonnay',
  sku: '25CHD3-750',
  price: 38.0,
  shopifyPrice: 38.0,
  stock: 130,
  image: '🍷',
  subscriptionCount: 120
}, {
  id: '9',
  name: '2024 Sauvignon Blanc',
  sku: '24SB2-750',
  price: 28.0,
  shopifyPrice: 28.0,
  stock: 100,
  image: '🍷',
  subscriptionCount: 95
}, {
  id: '10',
  name: '2024 Shiraz',
  sku: '24SHZ9-750',
  price: 45.0,
  shopifyPrice: 45.0,
  stock: 200,
  image: '🍷',
  subscriptionCount: 156
}, {
  id: '11',
  name: '2024 Pinot Noir',
  sku: '24PN5-750',
  price: 48.0,
  shopifyPrice: 48.0,
  stock: 180,
  image: '🍷',
  subscriptionCount: 67
}, {
  id: '12',
  name: '2023 Merlot',
  sku: '23MRL-750',
  price: 36.0,
  shopifyPrice: 36.0,
  stock: 250,
  image: '🍷',
  subscriptionCount: 78
}];
const subscriptionSummaryData: Record<string, {
  total: number;
  active: number;
  paused: number;
  failedPayment: number;
  nextBilling: string;
  totalUnits: number;
  plans: {
    name: string;
    count: number;
  }[];
}> = {
  '23RCAB-750': {
    total: 89,
    active: 82,
    paused: 5,
    failedPayment: 2,
    nextBilling: 'Feb 15, 2026',
    totalUnits: 134,
    plans: [{
      name: 'Mixed Dozen',
      count: 45
    }, {
      name: 'Premium Reds',
      count: 28
    }, {
      name: 'White Wine Lovers',
      count: 16
    }]
  },
  '24SPB-750': {
    total: 34,
    active: 31,
    paused: 3,
    failedPayment: 0,
    nextBilling: 'Feb 18, 2026',
    totalUnits: 34,
    plans: [{
      name: 'Celebration Club',
      count: 18
    }, {
      name: 'Mixed Dozen',
      count: 12
    }, {
      name: 'Sparkling Selection',
      count: 4
    }]
  }
};
// Mock affected subscriptions
const mockAffectedSubscriptions = [{
  id: 'SUB-23144',
  customer: 'Sarah Johnson',
  quantity: 2,
  currentPrice: 90.0,
  nextOrder: '2026-01-15',
  selected: true
}, {
  id: 'SUB-23141',
  customer: 'Michael Chen',
  quantity: 1,
  currentPrice: 45.0,
  nextOrder: '2026-01-18',
  selected: true
}, {
  id: 'SUB-23139',
  customer: 'Emma Williams',
  quantity: 3,
  currentPrice: 135.0,
  nextOrder: '2026-01-20',
  selected: true
}, {
  id: 'SUB-23138',
  customer: 'James Anderson',
  quantity: 2,
  currentPrice: 90.0,
  nextOrder: '2026-01-22',
  selected: true
}, {
  id: 'SUB-23137',
  customer: 'Olivia Martinez',
  quantity: 1,
  currentPrice: 45.0,
  nextOrder: '2026-01-25',
  selected: true
}];
export function ProductReplacePage() {
  const navigate = useNavigate();
  const location = useLocation();
  // Operation type state
  const [operationType, setOperationType] = useState<'replace' | 'sync'>('replace');
  // State
  const [howItWorksExpanded, setHowItWorksExpanded] = useState(true);
  const [originalProduct, setOriginalProduct] = useState<(typeof mockProducts)[0] | null>(null);
  const [replacementProduct, setReplacementProduct] = useState<(typeof mockProducts)[0] | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<(typeof mockProducts)[0] | null>(null);
  const [originalSearchQuery, setOriginalSearchQuery] = useState('');
  const [replacementSearchQuery, setReplacementSearchQuery] = useState('');
  const [syncSearchQuery, setSyncSearchQuery] = useState('');
  const [showOriginalDropdown, setShowOriginalDropdown] = useState(false);
  const [showReplacementDropdown, setShowReplacementDropdown] = useState(false);
  const [showSyncDropdown, setShowSyncDropdown] = useState(false);
  const [showSameProductPrompt, setShowSameProductPrompt] = useState(false);
  // Replace options state
  const [priceHandling, setPriceHandling] = useState('keep-original');
  const [customPercentage, setCustomPercentage] = useState(100);
  const [notifyCustomers, setNotifyCustomers] = useState(true);
  const [includeReason, setIncludeReason] = useState(true);
  const [replacementReason, setReplacementReason] = useState('');
  const [timing, setTiming] = useState('immediate');
  const [thresholdUnits, setThresholdUnits] = useState(5);
  const [scheduledDate, setScheduledDate] = useState('');
  // Affected subscriptions state
  const [affectedSubscriptions, setAffectedSubscriptions] = useState(mockAffectedSubscriptions);
  const [showAllSubscriptions, setShowAllSubscriptions] = useState(false);
  // Modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showThresholdBreachedModal, setShowThresholdBreachedModal] = useState(false);
  const [breachBehaviour, setBreachBehaviour] = useState<'wait-for-billing' | 'fire-immediately'>('wait-for-billing');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  // Recent replacements state
  const [recentReplacementsExpanded, setRecentReplacementsExpanded] = useState(false);
  // Calculate price difference
  const priceDifference = originalProduct && replacementProduct ? replacementProduct.price - originalProduct.price : 0;
  const hasPriceDifference = Math.abs(priceDifference) > 0.01;
  // Handle pre-selection from navigation state
  useEffect(() => {
    if (location.state && (location.state as any).preSelectedProduct) {
      const sku = (location.state as any).preSelectedProduct;
      const product = mockProducts.find(p => p.sku === sku);
      if (product) {
        setOriginalProduct(product);
      }
    }
  }, [location.state]);
  // Check if same product selected
  useEffect(() => {
    if (operationType === 'replace' && originalProduct && replacementProduct && originalProduct.id === replacementProduct.id) {
      setShowSameProductPrompt(true);
    } else {
      setShowSameProductPrompt(false);
    }
  }, [operationType, originalProduct, replacementProduct]);
  // Filter products for search
  const filteredOriginalProducts = mockProducts.filter(p => p.name.toLowerCase().includes(originalSearchQuery.toLowerCase()) || p.sku.toLowerCase().includes(originalSearchQuery.toLowerCase()));
  const filteredReplacementProducts = mockProducts.filter(p => p.name.toLowerCase().includes(replacementSearchQuery.toLowerCase()) || p.sku.toLowerCase().includes(replacementSearchQuery.toLowerCase()));
  const filteredSyncProducts = mockProducts.filter(p => p.name.toLowerCase().includes(syncSearchQuery.toLowerCase()) || p.sku.toLowerCase().includes(syncSearchQuery.toLowerCase()));
  const productsWithPriceMismatch = mockProducts.filter(p => p.hasPriceMismatch);
  // Calculate new price based on handling
  const getNewPrice = (currentPrice: number) => {
    if (operationType === 'sync' && selectedProduct) {
      return selectedProduct.shopifyPrice;
    }
    if (!replacementProduct) return currentPrice;
    if (priceHandling === 'keep-original') return currentPrice;
    if (priceHandling === 'use-new') return replacementProduct.price;
    if (priceHandling === 'custom') return replacementProduct.price * (customPercentage / 100);
    return currentPrice;
  };
  // Handle select/deselect all
  const handleSelectAll = () => {
    setAffectedSubscriptions(prev => prev.map(sub => ({
      ...sub,
      selected: true
    })));
  };
  const handleDeselectAll = () => {
    setAffectedSubscriptions(prev => prev.map(sub => ({
      ...sub,
      selected: false
    })));
  };
  const handleToggleSubscription = (id: string) => {
    setAffectedSubscriptions(prev => prev.map(sub => sub.id === id ? {
      ...sub,
      selected: !sub.selected
    } : sub));
  };
  // Handle execute
  const handleExecute = () => {
    if (timing === 'threshold' && originalProduct && originalProduct.stock <= thresholdUnits) {
      setShowThresholdBreachedModal(true);
      return;
    }
    setShowConfirmModal(true);
  };
  const handleConfirmExecute = () => {
    setIsProcessing(true);
    setShowConfirmModal(false);
    // Simulate processing
    setTimeout(() => {
      setIsProcessing(false);
      setShowSuccessBanner(true);
      // Reset form
      setOriginalProduct(null);
      setReplacementProduct(null);
      setSelectedProduct(null);
      setReplacementReason('');
    }, 2000);
  };
  const selectedCount = affectedSubscriptions.filter(s => s.selected).length;
  // Dynamic steps based on operation type
  const steps = operationType === 'replace' ? [{
    num: 1,
    label: 'Select Original'
  }, {
    num: 2,
    label: 'Select Replacement'
  }, {
    num: 3,
    label: 'Review Affected'
  }, {
    num: 4,
    label: 'Execute Replace'
  }] : [{
    num: 1,
    label: 'Select Product'
  }, {
    num: 2,
    label: 'Review Affected'
  }, {
    num: 3,
    label: 'Sync Prices'
  }];
  const activeProduct = operationType === 'sync' ? selectedProduct : originalProduct;
  return <div className="bg-[#F1F5F9] min-h-full p-6 pb-32" data-id="element-12188" data-mp="3730326eyl7zd">
      {/* NavBar */}
      <div className="mb-6" data-id="element-12189" data-mp="3c2lcxbvo30qv">
        <NavBar tabs={[]} title="Product Replace" subtitle="Swap products across multiple subscriptions at once" data-id="element-12190" data-mp="3c2use5j35pib" />
      </div>

      {/* Success Banner */}
      {showSuccessBanner && <div className="mb-6 p-4 bg-[#D1FAE5] border border-[#10B981] rounded-lg flex items-start gap-3" data-id="element-12191" data-mp="32cjo2hvhpeaw">
          <CheckCircle className="w-5 h-5 text-[#065F46] flex-shrink-0 mt-0.5" data-id="element-12192" data-mp="3q1uhfeaig6f0" />
          <div className="flex-1" data-id="element-12193" data-mp="3ut2qg97xhprn">
            <h3 className="text-[14px] font-semibold text-[#065F46] mb-1" data-id="element-12194" data-mp="3utcmadfnmr59">
              {operationType === 'replace' ? 'Product replacement complete' : 'Price sync complete'}
            </h3>
            <p className="text-[14px] text-[#065F46]" data-id="element-12195" data-mp="1efsnh9rzlwne">
              {selectedCount} subscriptions have been updated. Customers will be
              notified shortly.
            </p>
            <button onClick={() => navigate('/operations/activity-log')} className="text-[14px] font-medium text-[#0EA5E9] hover:text-[#0284C7] mt-2" data-id="element-12196" data-mp="1o53pydkdc32b">
              View activity log →
            </button>
          </div>
          <button onClick={() => setShowSuccessBanner(false)} className="p-1 hover:bg-[#10B981] hover:bg-opacity-20 rounded transition-colors" data-id="element-12197" data-mp="vh7z3phhd5dg">
            <X className="w-5 h-5 text-[#065F46]" data-id="element-12198" data-mp="1abcirbu40v9r" />
          </button>
        </div>}

      <div className="max-w-6xl mx-auto space-y-6" data-id="element-12199" data-mp="15d184yvzqgqh">
        {/* Section 1: How It Works */}
        <Card data-id="element-12200" data-mp="2lr16w6j3o0c5">
          <button onClick={() => setHowItWorksExpanded(!howItWorksExpanded)} className="w-full flex items-center justify-between" data-id="element-12201" data-mp="2lup014kmukqc">
            <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-12202" data-mp="2nwy11iylway9">
              How{' '}
              {operationType === 'replace' ? 'Product Replace' : 'Price Sync'}{' '}
              Works
            </h2>
            {howItWorksExpanded ? <ChevronUp className="w-5 h-5 text-[#64748B]" data-id="element-12203" data-mp="2xw6so5601dhg" /> : <ChevronDown className="w-5 h-5 text-[#64748B]" data-id="element-12204" data-mp="32tyj0uqzwrjj" />}
          </button>

          {howItWorksExpanded && <div className="mt-6" data-id="element-12205" data-mp="2567vtnlkcc28">
              {/* Step indicators */}
              <div className="flex items-center justify-between mb-6" data-id="element-12206" data-mp="2543m2p0zgiu1">
                {steps.map((step, index) => <Fragment key={step.num} data-id="element-12207" data-mp="2a36t2f0iwz4n" data-mpi={index}>
                    <div className="flex flex-col items-center" data-id="element-12208" data-mp="2f4484iq8ld1x">
                      <div className="w-10 h-10 rounded-full bg-[#0EA5E9] text-white flex items-center justify-center font-semibold mb-2" data-id="element-12209" data-mp="2f2jc0oo9xm1y">
                        {step.num}
                      </div>
                      <span className="text-[12px] text-[#64748B] text-center" data-id="element-12210" data-mp="1retfjoj1ioyv">
                        {step.label}
                      </span>
                    </div>
                    {index < steps.length - 1 && <div className="flex-1 h-0.5 bg-[#E2E8F0] mx-2 mb-6" data-id="element-12211" data-mp="4i3p8lis2z2l" />}
                  </Fragment>)}
              </div>

              {/* Info banner */}
              <div className="p-4 bg-[#E0F2FE] border border-[#7DD3FC] rounded-md flex items-start gap-3" data-id="element-12212" data-mp="3vmtoc6z59em1">
                <Info className="w-5 h-5 text-[#0369A1] flex-shrink-0 mt-0.5" data-id="element-12213" data-mp="3qos9jy8r41gd" />
                <p className="text-[14px] text-[#0369A1]" data-id="element-12214" data-mp="3lr0faxkmw390">
                  {operationType === 'replace' ? "Product Replace is commonly used when a wine vintage sells out. Replace '2022 Shiraz' with '2023 Shiraz' across all subscriptions in one action." : "Price Sync updates subscription prices to match your current Shopify prices. Use this when you've changed product prices and want existing subscriptions to reflect the new pricing."}
                </p>
              </div>
            </div>}
        </Card>

        {/* NEW: Operation Type Section */}
        <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-6" data-id="element-12215" data-mp="11xhrxgbtj3z4">
          <h2 className="text-[20px] font-semibold text-[#0F172A] mb-4" data-id="element-12216" data-mp="11zua9ulhngcp">
            Operation Type
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-12217" data-mp="s7omkx29rl3b">
            {/* Replace Product Option */}
            <label className={`flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer transition-all ${operationType === 'replace' ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`} data-id="element-12218" data-mp="x69kgpu1tsol">
              <input type="radio" name="operationType" value="replace" checked={operationType === 'replace'} onChange={e => setOperationType(e.target.value as 'replace' | 'sync')} className="mt-1 w-5 h-5 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-12219" data-mp="o74lxjihlzcf" />
              <div data-id="element-12220" data-mp="23mtzetw4700n">
                <div className="flex items-center gap-2 mb-1" data-id="element-12221" data-mp="23mbwjeey5njr">
                  <ArrowRightLeft className="w-5 h-5 text-[#0EA5E9]" data-id="element-12222" data-mp="23l0r7a5us7qj" />
                  <span className="text-[16px] font-semibold text-[#0F172A]" data-id="element-12223" data-mp="28lepi0hnk3bd">
                    Replace Product
                  </span>
                </div>
                <p className="text-[14px] text-[#64748B]" data-id="element-12224" data-mp="3o5e9ccahb193">
                  Swap one product for another across subscriptions
                </p>
                <p className="text-[12px] text-[#94A3B8] mt-1" data-id="element-12225" data-mp="6ppcle7qj4l5">
                  e.g., Replace sold-out 2022 vintage with 2023 vintage
                </p>
              </div>
            </label>

            {/* Sync Prices Option */}
            <label className={`flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer transition-all ${operationType === 'sync' ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`} data-id="element-12226" data-mp="bnhen123g732">
              <input type="radio" name="operationType" value="sync" checked={operationType === 'sync'} onChange={e => setOperationType(e.target.value as 'replace' | 'sync')} className="mt-1 w-5 h-5 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-12227" data-mp="knnqyjm2310n" />
              <div data-id="element-12228" data-mp="2mhmz4w3vt294">
                <div className="flex items-center gap-2 mb-1" data-id="element-12229" data-mp="qcdvmb8pygnq">
                  <RefreshCw className="w-5 h-5 text-[#0EA5E9]" data-id="element-12230" data-mp="qdyqy32uy3mn" />
                  <span className="text-[16px] font-semibold text-[#0F172A]" data-id="element-12231" data-mp="vap68o6u8v70">
                    Sync Prices
                  </span>
                </div>
                <p className="text-[14px] text-[#64748B]" data-id="element-12232" data-mp="zff0y9prg3qc">
                  Update subscription prices to match current Shopify prices
                </p>
                <p className="text-[12px] text-[#94A3B8] mt-1" data-id="element-12233" data-mp="19a6vf4o1qagw">
                  e.g., Apply recent price changes to existing subscriptions
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Section 2: Product Selection - Conditional based on operation type */}
        {operationType === 'replace' ? <Card data-id="element-12234" data-mp="1n4hgnc0u3mi7">
            <h2 className="text-[20px] font-semibold text-[#0F172A] mb-6" data-id="element-12235" data-mp="1s1zrx92bqdtd">
              Select Products
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-6" data-id="element-12236" data-mp="1x01brw7kqh5w">
              {/* Original Product */}
              <div data-id="element-12237" data-mp="275kduk7flcja">
                <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-12238" data-mp="2762gpzolmp06">
                  Original Product
                </h3>
                <p className="text-[13px] text-[#64748B] mb-4" data-id="element-12239" data-mp="1x6bmlz3yac0m">
                  The product currently in subscriptions that you want to
                  replace
                </p>

                {/* Search */}
                <div className="relative mb-4" data-id="element-12240" data-mp="2pvilx0b8b5mx">
                  <input type="text" placeholder="🔍 Search products..." value={originalSearchQuery} onChange={e => {
                setOriginalSearchQuery(e.target.value);
                setShowOriginalDropdown(true);
              }} onFocus={() => setShowOriginalDropdown(true)} className="w-full h-[40px] px-4 border border-[#CBD5E1] rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-12241" data-mp="2pxd7mgubr1s2" />

                  {/* Dropdown */}
                  {showOriginalDropdown && originalSearchQuery && <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowOriginalDropdown(false)} data-id="element-12242" data-mp="3niskos21ges6" />
                      <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-[#CBD5E1] rounded-md shadow-lg z-20 max-h-64 overflow-y-auto" data-id="element-12243" data-mp="3sau5x3h6pfuc">
                        {filteredOriginalProducts.map(product => <button key={product.id} onClick={() => {
                    setOriginalProduct(product);
                    setOriginalSearchQuery('');
                    setShowOriginalDropdown(false);
                  }} className="w-full p-3 hover:bg-[#F8FAFC] transition-colors text-left flex items-center gap-3 border-b border-[#E2E8F0] last:border-0" data-id="element-12244" data-mp="3il0sry1e8snz">
                            <span className="text-2xl" data-id="element-12245" data-mp="54jor7i1lihg">{product.image}</span>
                            <div className="flex-1" data-id="element-12246" data-mp="2kw7983fvdijd">
                              <div className="text-[14px] font-medium text-[#0F172A]" data-id="element-12247" data-mp="2kvfqvuba9hb1">
                                {product.name}
                              </div>
                              <div className="text-[12px] text-[#94A3B8]" data-id="element-12248" data-mp="17x5nei30meig">
                                {product.sku} · ${product.price.toFixed(2)}
                              </div>
                            </div>
                            {product.stock < 20 && <Badge variant="warning" data-id="element-12249" data-mp="1gxkdrvuu6jst">Low stock</Badge>}
                          </button>)}
                      </div>
                    </>}
                </div>

                {/* Selected or Empty State */}
                {originalProduct ? <div className="p-4 border-2 border-[#0EA5E9] bg-[#F0F9FF] rounded-lg" data-id="element-12250" data-mp="k8crumqb28od">
                    <div className="flex items-start justify-between mb-3" data-id="element-12251" data-mp="xzhix0flxsn3">
                      <div className="flex items-start gap-3" data-id="element-12252" data-mp="y1lxqdf1oc25">
                        <span className="text-3xl" data-id="element-12253" data-mp="t7pyz8qyil5x">
                          {originalProduct.image}
                        </span>
                        <div data-id="element-12254" data-mp="5h3tnp8c7ezm">
                          <h4 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-12255" data-mp="1huycfxnf91y">
                            {originalProduct.name}
                          </h4>
                          <p className="text-[13px] text-[#64748B]" data-id="element-12256" data-mp="2rmqnv8izzbrl">
                            750ml · SKU: {originalProduct.sku}
                          </p>
                          <p className="text-[14px] font-semibold text-[#0F172A] mt-1" data-id="element-12257" data-mp="31fo8bk7jvasu">
                            ${originalProduct.price.toFixed(2)}
                          </p>
                        </div>
                      </div>
                      <button onClick={() => setOriginalProduct(null)} className="p-1 hover:bg-[#0EA5E9] hover:bg-opacity-20 rounded transition-colors" data-id="element-12258" data-mp="3bewz6474c9az">
                        <X className="w-5 h-5 text-[#0EA5E9]" data-id="element-12259" data-mp="3kdqqbf1ok7h5" />
                      </button>
                    </div>
                    <div className="space-y-2 pt-3 border-t border-[#7DD3FC]" data-id="element-12260" data-mp="3ffp78u49885o">
                      <div className="flex items-center gap-2 text-[13px] text-[#0369A1]" data-id="element-12261" data-mp="1q0itjhu59lsv">
                        <span data-id="element-12262" data-mp="1l16891ix8mse">📊</span>
                        <span data-id="element-12263" data-mp="1l27k387rhdb6">
                          In {originalProduct.subscriptionCount} active
                          subscriptions
                        </span>
                      </div>
                      {originalProduct.stock < 20 && <div className="flex items-center gap-2 text-[13px] text-[#92400E]" data-id="element-12264" data-mp="3nvehd7evuzom">
                          <AlertTriangle className="w-4 h-4" data-id="element-12265" data-mp="3rx02fg1kmk9w" />
                          <span data-id="element-12266" data-mp="3rvgmxrnqod96">
                            Low stock: {originalProduct.stock} remaining
                          </span>
                        </div>}
                    </div>
                  </div> : <div className="p-8 border-2 border-dashed border-[#CBD5E1] rounded-lg text-center" data-id="element-12267" data-mp="3ixwh05exlss8">
                    <Package className="w-12 h-12 text-[#CBD5E1] mx-auto mb-3" data-id="element-12268" data-mp="34ydb7j9letj3" />
                    <p className="text-[14px] text-[#94A3B8]" data-id="element-12269" data-mp="38yx6sieipp34">
                      Select a product to replace
                    </p>
                    <p className="text-[13px] text-[#CBD5E1] mt-1" data-id="element-12270" data-mp="133cp8ujd9rp7">
                      Search above or browse your catalog
                    </p>
                  </div>}

                {/* Subscription Summary Panel */}
                {originalProduct && subscriptionSummaryData[originalProduct.sku] && <div className="mt-4 bg-white border border-[#E2E8F0] rounded-lg shadow-sm overflow-hidden animate-in fade-in slide-in-from-top-2 duration-300" data-id="element-12271" data-mp="2b6quupzpvcwr">
                      <div className="px-4 py-3 bg-[#F8FAFC] border-b border-[#E2E8F0]" data-id="element-12272" data-mp="2259i9hliptct">
                        <h4 className="text-[14px] font-semibold text-[#0F172A]" data-id="element-12273" data-mp="2yt5p3ur5laau">
                          Subscriptions containing {originalProduct.name} (
                          {subscriptionSummaryData[originalProduct.sku].total})
                        </h4>
                      </div>
                      <div className="p-4 space-y-4" data-id="element-12274" data-mp="2l3lrt4gcd58x">
                        {/* Status Breakdown */}
                        <div className="flex flex-wrap gap-2" data-id="element-12275" data-mp="3ij9f2jp2h2vu">
                          <Badge variant="success" data-id="element-12276" data-mp="3drhnc5u6cr44">
                            {subscriptionSummaryData[originalProduct.sku].active}{' '}
                            Active
                          </Badge>
                          <Badge variant="warning" data-id="element-12277" data-mp="3q1gzr5nmwgkp">
                            {subscriptionSummaryData[originalProduct.sku].paused}{' '}
                            Paused
                          </Badge>
                          <Badge variant="error" data-id="element-12278" data-mp="2xj0jmu92tkv">
                            {subscriptionSummaryData[originalProduct.sku].failedPayment}{' '}
                            Failed Payment
                          </Badge>
                        </div>

                        {/* Key Metrics */}
                        <div className="grid grid-cols-2 gap-4 pt-2" data-id="element-12279" data-mp="hldsh10fotww">
                          <div data-id="element-12280" data-mp="ho9rkso0pomx">
                            <p className="text-[12px] font-medium text-[#64748B] uppercase tracking-wide" data-id="element-12281" data-mp="mlia4ud2vuop">
                              Next Billing
                            </p>
                            <p className="text-[14px] font-medium text-[#0F172A]" data-id="element-12282" data-mp="vlon8f4v6snc">
                              {subscriptionSummaryData[originalProduct.sku].nextBilling}
                            </p>
                          </div>
                          <div data-id="element-12283" data-mp="dwbehdqz6udj">
                            <p className="text-[12px] font-medium text-[#64748B] uppercase tracking-wide" data-id="element-12284" data-mp="dx4dfsjp0fl4">
                              Total Units
                            </p>
                            <p className="text-[14px] font-medium text-[#0F172A]" data-id="element-12285" data-mp="mq7x4ikxloo7">
                              {subscriptionSummaryData[originalProduct.sku].totalUnits}
                            </p>
                          </div>
                        </div>

                        {/* Plans */}
                        <div data-id="element-12286" data-mp="8zunmbse61wr">
                          <p className="text-[12px] font-medium text-[#64748B] uppercase tracking-wide mb-1" data-id="element-12287" data-mp="8vx4vr9q7cde">
                            Plans
                          </p>
                          <p className="text-[13px] text-[#475569] leading-relaxed" data-id="element-12288" data-mp="1kblqae7c03c3">
                            {subscriptionSummaryData[originalProduct.sku].plans.map((plan, i, arr) => <span key={plan.name} data-id="element-12289" data-mp="2xbp8lt0d8wnr" data-mpi={i}>
                                {plan.name}{' '}
                                <span className="text-[#94A3B8]" data-id="element-12290" data-mp="32bbk9y0jq4ug">
                                  ({plan.count})
                                </span>
                                {i < arr.length - 1 ? ', ' : ''}
                              </span>)}
                          </p>
                        </div>
                      </div>
                    </div>}
              </div>

              {/* Arrow */}
              <div className="hidden lg:flex items-center justify-center" data-id="element-12291" data-mp="3l0hx54ens28c">
                <ArrowRight className="w-8 h-8 text-[#94A3B8]" data-id="element-12292" data-mp="37ao9uqisxq0q" />
              </div>

              {/* Replacement Product */}
              <div data-id="element-12293" data-mp="3c7y5inwsaxx0">
                <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-12294" data-mp="3c7eoraivefkk">
                  Replacement Product
                </h3>
                <p className="text-[13px] text-[#64748B] mb-4" data-id="element-12295" data-mp="8jw6zv7jmjuh">
                  The product that will replace the original
                </p>

                {/* Search */}
                <div className="relative mb-4" data-id="element-12296" data-mp="idsbi0w4p31z">
                  <input type="text" placeholder="🔍 Search products..." value={replacementSearchQuery} onChange={e => {
                setReplacementSearchQuery(e.target.value);
                setShowReplacementDropdown(true);
              }} onFocus={() => setShowReplacementDropdown(true)} className="w-full h-[40px] px-4 border border-[#CBD5E1] rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-12297" data-mp="di38fnkwh40x" />

                  {/* Dropdown */}
                  {showReplacementDropdown && replacementSearchQuery && <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowReplacementDropdown(false)} data-id="element-12298" data-mp="3gy0izxmz4dzs" />
                      <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-[#CBD5E1] rounded-md shadow-lg z-20 max-h-64 overflow-y-auto" data-id="element-12299" data-mp="3bz73jzo4mcwz">
                        {filteredReplacementProducts.map(product => <button key={product.id} onClick={() => {
                    setReplacementProduct(product);
                    setReplacementSearchQuery('');
                    setShowReplacementDropdown(false);
                  }} className="w-full p-3 hover:bg-[#F8FAFC] transition-colors text-left flex items-center gap-3 border-b border-[#E2E8F0] last:border-0" data-id="element-12300" data-mp="1nft81ujsl8yt">
                            <span className="text-2xl" data-id="element-12301" data-mp="13rc3dtq1fzon">{product.image}</span>
                            <div className="flex-1" data-id="element-12302" data-mp="13p7q4l691obp">
                              <div className="text-[14px] font-medium text-[#0F172A]" data-id="element-12303" data-mp="18r6pzl2s633w">
                                {product.name}
                              </div>
                              <div className="text-[12px] text-[#94A3B8]" data-id="element-12304" data-mp="1iggllawu23wr">
                                {product.sku} · ${product.price.toFixed(2)}
                              </div>
                            </div>
                            {product.stock > 50 && <Badge variant="success" data-id="element-12305" data-mp="py2ya594i0bx">In stock</Badge>}
                          </button>)}
                      </div>
                    </>}
                </div>

                {/* Selected or Empty State */}
                {replacementProduct ? <div className="p-4 border-2 border-[#0EA5E9] bg-[#F0F9FF] rounded-lg" data-id="element-12306" data-mp="3oidyta3ss7hi">
                    <div className="flex items-start justify-between mb-3" data-id="element-12307" data-mp="3jjaw3wbaidcp">
                      <div className="flex items-start gap-3" data-id="element-12308" data-mp="3jhzv2498bjpa">
                        <span className="text-3xl" data-id="element-12309" data-mp="2a5t5sdba3v3">
                          {replacementProduct.image}
                        </span>
                        <div data-id="element-12310" data-mp="3tfe95x7ojwef">
                          <h4 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-12311" data-mp="b39esny41n0t">
                            {replacementProduct.name}
                          </h4>
                          <p className="text-[13px] text-[#64748B]" data-id="element-12312" data-mp="740k9gv8xl47">
                            750ml · SKU: {replacementProduct.sku}
                          </p>
                          <p className="text-[14px] font-semibold text-[#0F172A] mt-1" data-id="element-12313" data-mp="g3nenipk5qnr">
                            ${replacementProduct.price.toFixed(2)}
                          </p>
                        </div>
                      </div>
                      <button onClick={() => setReplacementProduct(null)} className="p-1 hover:bg-[#0EA5E9] hover:bg-opacity-20 rounded transition-colors" data-id="element-12314" data-mp="14l4ir8bt7qyf">
                        <X className="w-5 h-5 text-[#0EA5E9]" data-id="element-12315" data-mp="uo9hiho5jcgg" />
                      </button>
                    </div>
                    <div className="pt-3 border-t border-[#7DD3FC]" data-id="element-12316" data-mp="gwbfsrwrsiub">
                      <div className="flex items-center gap-2 text-[13px] text-[#059669]" data-id="element-12317" data-mp="lpo6d7l0ezpp">
                        <CheckCircle className="w-4 h-4" data-id="element-12318" data-mp="70hxerwddv4m" />
                        <span data-id="element-12319" data-mp="6xvp2q1nnblc">
                          In stock: {replacementProduct.stock} available
                        </span>
                      </div>
                    </div>
                  </div> : <div className="p-8 border-2 border-dashed border-[#CBD5E1] rounded-lg text-center" data-id="element-12320" data-mp="3u6ivzlcsqnlu">
                    <Package className="w-12 h-12 text-[#CBD5E1] mx-auto mb-3" data-id="element-12321" data-mp="22gfwbele3v9" />
                    <p className="text-[14px] text-[#94A3B8]" data-id="element-12322" data-mp="2l5t995mxki5s">
                      Select a replacement product
                    </p>
                    <p className="text-[13px] text-[#CBD5E1] mt-1" data-id="element-12323" data-mp="2v2ftbs7fn56r">
                      Search above or browse your catalog
                    </p>
                  </div>}
              </div>
            </div>

            {/* Same Product Prompt */}
            {showSameProductPrompt && <div className="mt-6 bg-[#F0F9FF] border border-[#0EA5E9] rounded-lg p-4 flex items-center justify-between" data-id="element-12324" data-mp="341084i9vgupp">
                <div className="flex items-center gap-3" data-id="element-12325" data-mp="3dsxm1ws9m0s4">
                  <Lightbulb className="w-5 h-5 text-[#0EA5E9]" data-id="element-12326" data-mp="3dtfo5a1lz97y" />
                  <div data-id="element-12327" data-mp="3iqpauhxm56s3">
                    <p className="text-[14px] font-medium text-[#0F172A]" data-id="element-12328" data-mp="3ir7dpxes6j8z">
                      Looks like you want to sync prices
                    </p>
                    <p className="text-[13px] text-[#64748B]" data-id="element-12329" data-mp="3rrxap4fs2foa">
                      You've selected the same product for both. Switch to Price
                      Sync mode?
                    </p>
                  </div>
                </div>
                <div className="flex gap-2" data-id="element-12330" data-mp="1c26tza01x3qu">
                  <button onClick={() => {
              setOperationType('sync');
              setSelectedProduct(originalProduct);
              setOriginalProduct(null);
              setReplacementProduct(null);
            }} className="px-4 py-2 bg-[#0EA5E9] text-white text-[14px] font-medium rounded-md hover:bg-[#0284C7]" data-id="element-12331" data-mp="1745f719nrql6">
                    Switch to Price Sync
                  </button>
                  <button onClick={() => setShowSameProductPrompt(false)} className="px-4 py-2 text-[14px] text-[#64748B] hover:text-[#0F172A]" data-id="element-12332" data-mp="1q2q02bkyhzqd">
                    Continue
                  </button>
                </div>
              </div>}

            {/* Price Difference Warning */}
            {originalProduct && replacementProduct && hasPriceDifference && !showSameProductPrompt && <div className="mt-6 p-4 bg-[#FEF3C7] border border-[#FDE68A] rounded-md flex items-start gap-3" data-id="element-12333" data-mp="2dr0titobox4m">
                  <AlertTriangle className="w-5 h-5 text-[#92400E] flex-shrink-0 mt-0.5" data-id="element-12334" data-mp="fwncgunvhfv1" />
                  <p className="text-[14px] text-[#92400E]" data-id="element-12335" data-mp="25i5t8m37nb7">
                    <span className="font-semibold" data-id="element-12336" data-mp="24yx8iibub9u">Price difference:</span> The
                    replacement product is $
                    {Math.abs(priceDifference).toFixed(2)}{' '}
                    {priceDifference > 0 ? 'more expensive' : 'cheaper'}. Choose
                    how to handle this below.
                  </p>
                </div>}
          </Card> /* SYNC MODE - Single Product Selection */ : <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-6" data-id="element-12337" data-mp="3fduz4i7a7pw0">
            <h2 className="text-[20px] font-semibold text-[#0F172A] mb-2" data-id="element-12338" data-mp="3kbn34al7b2gk">
              Select Product
            </h2>
            <p className="text-[14px] text-[#64748B] mb-6" data-id="element-12339" data-mp="1dikme3o6scvd">
              Choose a product to sync prices for
            </p>

            {/* Single product search */}
            <div className="relative mb-6" data-id="element-12340" data-mp="1sogq0wv8ael9">
              <input type="text" placeholder="🔍 Search products..." value={syncSearchQuery} onChange={e => {
            setSyncSearchQuery(e.target.value);
            setShowSyncDropdown(true);
          }} onFocus={() => setShowSyncDropdown(true)} className="w-full h-[40px] px-4 border border-[#CBD5E1] rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-12341" data-mp="1nokjoqz26b2j" />

              {/* Dropdown */}
              {showSyncDropdown && syncSearchQuery && <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowSyncDropdown(false)} data-id="element-12342" data-mp="2keutdz5m88io" />
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-[#CBD5E1] rounded-md shadow-lg z-20 max-h-64 overflow-y-auto" data-id="element-12343" data-mp="2gd9lytbuomfv">
                    {filteredSyncProducts.map(product => <button key={product.id} onClick={() => {
                setSelectedProduct(product);
                setSyncSearchQuery('');
                setShowSyncDropdown(false);
              }} className="w-full p-3 hover:bg-[#F8FAFC] transition-colors text-left flex items-center gap-3 border-b border-[#E2E8F0] last:border-0" data-id="element-12344" data-mp="lz8fbma26v58">
                        <span className="text-2xl" data-id="element-12345" data-mp="widlimejlc4k">{product.image}</span>
                        <div className="flex-1" data-id="element-12346" data-mp="11gotssp6veko">
                          <div className="text-[14px] font-medium text-[#0F172A]" data-id="element-12347" data-mp="11fdq0svqu6tk">
                            {product.name}
                          </div>
                          <div className="text-[12px] text-[#94A3B8]" data-id="element-12348" data-mp="8qq3ktz9dbef">
                            {product.sku} · ${product.price.toFixed(2)}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </>}
            </div>

            {/* Selected Product */}
            {selectedProduct && <div className="p-4 border-2 border-[#0EA5E9] bg-[#F0F9FF] rounded-lg mb-6" data-id="element-12349" data-mp="3h58dz2co9317">
                <div className="flex items-start justify-between" data-id="element-12350" data-mp="3m4kxpe88xtzk">
                  <div className="flex items-start gap-3" data-id="element-12351" data-mp="3eltwnfzofzys">
                    <span className="text-3xl" data-id="element-12352" data-mp="39nsaunav9mjm">{selectedProduct.image}</span>
                    <div data-id="element-12353" data-mp="39lpbhcntqh28">
                      <h4 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-12354" data-mp="34ofhdjphph82">
                        {selectedProduct.name}
                      </h4>
                      <p className="text-[13px] text-[#64748B]" data-id="element-12355" data-mp="30mkjkp6rplz1">
                        750ml · SKU: {selectedProduct.sku}
                      </p>
                      <p className="text-[14px] font-semibold text-[#0F172A] mt-1" data-id="element-12356" data-mp="2qwsird82rwm4">
                        Shopify Price: $
                        {selectedProduct.shopifyPrice.toFixed(2)}
                      </p>
                    </div>
                  </div>
                  <button onClick={() => setSelectedProduct(null)} className="p-1 hover:bg-[#0EA5E9] hover:bg-opacity-20 rounded transition-colors" data-id="element-12357" data-mp="2gx1pfev93nnm">
                    <X className="w-5 h-5 text-[#0EA5E9]" data-id="element-12358" data-mp="27y6h9x8nc3ho" />
                  </button>
                </div>
              </div>}

            {/* Products with Price Differences - Quick Select */}
            {productsWithPriceMismatch.length > 0 && <div className="border-t border-[#E2E8F0] pt-6" data-id="element-12359" data-mp="22dqsal9nxjoo">
                <div className="flex items-center gap-2 mb-4" data-id="element-12360" data-mp="1odg2zmo04d5m">
                  <AlertTriangle className="w-5 h-5 text-[#F59E0B]" data-id="element-12361" data-mp="1tbpxrw5tdbqp" />
                  <span className="text-[14px] font-medium text-[#0F172A]" data-id="element-12362" data-mp="1tbzpppaf5sz1">
                    {productsWithPriceMismatch.length} products have price
                    differences
                  </span>
                </div>

                <div className="space-y-3" data-id="element-12363" data-mp="27bii99qqyvq8">
                  {productsWithPriceMismatch.map(product => <button key={product.id} onClick={() => setSelectedProduct(product)} className="w-full flex items-center justify-between p-4 border border-[#E2E8F0] rounded-lg hover:bg-[#F8FAFC] cursor-pointer transition-colors" data-id="element-12364" data-mp="279o90sou8s1z">
                      <div className="flex items-center gap-4" data-id="element-12365" data-mp="34reh8djuzsmr">
                        <div className="w-12 h-12 bg-[#F1F5F9] rounded-md flex items-center justify-center text-2xl" data-id="element-12366" data-mp="34sppapkc7mjo">
                          {product.image}
                        </div>
                        <div className="text-left" data-id="element-12367" data-mp="oaqbi4rcdoaj">
                          <p className="text-[14px] font-medium text-[#0F172A]" data-id="element-12368" data-mp="124hesvehcbxt">
                            {product.name}
                          </p>
                          <p className="text-[13px] text-[#64748B]" data-id="element-12369" data-mp="y5067t3rayof">
                            {product.subscriptionCount} subscriptions affected
                          </p>
                        </div>
                      </div>
                      <div className="text-right" data-id="element-12370" data-mp="je9fkodwi5mw">
                        <p className="text-[14px] text-[#64748B]" data-id="element-12371" data-mp="jbn5agzmlc0z">
                          <span className="line-through" data-id="element-12372" data-mp="edvexrempxyw">
                            ${product.subscriptionPrice?.toFixed(2)}
                          </span>
                          <span className="mx-2" data-id="element-12373" data-mp="1pu2744wiwljr">→</span>
                          <span className="font-semibold text-[#0F172A]" data-id="element-12374" data-mp="1ptk2okzpj10r">
                            ${product.shopifyPrice.toFixed(2)}
                          </span>
                        </p>
                        <p className="text-[13px] text-[#F59E0B]" data-id="element-12375" data-mp="2evj6wflyctbh">
                          +$
                          {(product.shopifyPrice - (product.subscriptionPrice || 0)).toFixed(2)}
                        </p>
                      </div>
                    </button>)}
                </div>

                {/* Sync All Button */}
                <button className="mt-4 w-full h-[48px] flex items-center justify-center gap-2 border-2 border-dashed border-[#CBD5E1] rounded-lg text-[14px] font-medium text-[#0EA5E9] hover:bg-[#F0F9FF] hover:border-[#0EA5E9] transition-all" data-id="element-12376" data-mp="2xlinee3q903a">
                  <RefreshCw className="w-4 h-4" data-id="element-12377" data-mp="36kw15iyvwatt" />
                  Sync All Products with Price Differences
                </button>
              </div>}
          </div>}

        {/* Price Comparison Section (Sync Mode Only) */}
        {operationType === 'sync' && selectedProduct && <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-6" data-id="element-12378" data-mp="14p6pf00pdi56">
            <h2 className="text-[20px] font-semibold text-[#0F172A] mb-6" data-id="element-12379" data-mp="zy6pdlwaejyg">
              Price Comparison
            </h2>

            {/* Visual comparison */}
            <div className="flex items-center justify-center gap-8 py-8 bg-[#F8FAFC] rounded-lg mb-6" data-id="element-12380" data-mp="uz3nubfimvv8">
              <div className="text-center" data-id="element-12381" data-mp="qv5lzsyt36yh">
                <p className="text-[12px] font-medium text-[#64748B] uppercase tracking-wide mb-2" data-id="element-12382" data-mp="1no0zhvp81uf0">
                  Subscription Price
                </p>
                <div className="bg-white border border-[#E2E8F0] rounded-lg px-8 py-4" data-id="element-12383" data-mp="1imbnodu6dcpv">
                  <p className="text-[28px] font-semibold text-[#64748B]" data-id="element-12384" data-mp="1ekzz8bn1exj9">
                    $
                    {(selectedProduct.subscriptionPrice || selectedProduct.price).toFixed(2)}
                  </p>
                  <p className="text-[12px] text-[#94A3B8]" data-id="element-12385" data-mp="35b9jciefker">current</p>
                </div>
              </div>

              <ArrowRight className="w-8 h-8 text-[#CBD5E1]" data-id="element-12386" data-mp="3muhd7108wge1" />

              <div className="text-center" data-id="element-12387" data-mp="3mtxydt5w6848">
                <p className="text-[12px] font-medium text-[#64748B] uppercase tracking-wide mb-2" data-id="element-12388" data-mp="3it5mst9jrr4s">
                  Shopify Price
                </p>
                <div className="bg-white border-2 border-[#0EA5E9] rounded-lg px-8 py-4" data-id="element-12389" data-mp="3ds884qnqxf81">
                  <p className="text-[28px] font-semibold text-[#0F172A]" data-id="element-12390" data-mp="38u6mpz2ul3te">
                    ${selectedProduct.shopifyPrice.toFixed(2)}
                  </p>
                  <p className="text-[12px] text-[#0EA5E9]" data-id="element-12391" data-mp="2z4dbx0aj4sq7">new</p>
                </div>
              </div>
            </div>

            {/* Impact summary */}
            <div className="grid grid-cols-3 gap-4 text-center" data-id="element-12392" data-mp="2v17962mmljoh">
              <div className="p-4 bg-[#F8FAFC] rounded-lg" data-id="element-12393" data-mp="2q59v62yu4pev">
                <p className="text-[24px] font-semibold text-[#0F172A]" data-id="element-12394" data-mp="2l4ugn82thbvb">
                  +$
                  {(selectedProduct.shopifyPrice - (selectedProduct.subscriptionPrice || selectedProduct.price)).toFixed(2)}
                </p>
                <p className="text-[13px] text-[#64748B]" data-id="element-12395" data-mp="261aukqxmfihy">
                  per unit (+
                  {((selectedProduct.shopifyPrice - (selectedProduct.subscriptionPrice || selectedProduct.price)) / (selectedProduct.subscriptionPrice || selectedProduct.price) * 100).toFixed(1)}
                  %)
                </p>
              </div>
              <div className="p-4 bg-[#F8FAFC] rounded-lg" data-id="element-12396" data-mp="2kx0kv19uahsh">
                <p className="text-[24px] font-semibold text-[#0F172A]" data-id="element-12397" data-mp="1ncxlqdr31wii">
                  {selectedProduct.subscriptionCount}
                </p>
                <p className="text-[13px] text-[#64748B]" data-id="element-12398" data-mp="1x9shiovz1it3">subscriptions</p>
              </div>
              <div className="p-4 bg-[#F8FAFC] rounded-lg" data-id="element-12399" data-mp="220ik5ud0j7k3">
                <p className="text-[24px] font-semibold text-[#10B981]" data-id="element-12400" data-mp="22220fkyo5ilv">
                  +$
                  {((selectedProduct.shopifyPrice - (selectedProduct.subscriptionPrice || selectedProduct.price)) * 28).toFixed(2)}
                </p>
                <p className="text-[13px] text-[#64748B]" data-id="element-12401" data-mp="11j8mg3aw9cfl">revenue/cycle</p>
              </div>
            </div>
          </div>}

        {/* Section 3: Replace Options */}
        {originalProduct && replacementProduct && <Card data-id="element-12402" data-mp="1b9uvtfs0p6pu">
            <h2 className="text-[20px] font-semibold text-[#0F172A] mb-6" data-id="element-12403" data-mp="1b89zplq21fpv">
              Replace Options
            </h2>

            {/* Price Handling - Only show in replace mode when prices differ */}
            {operationType === 'replace' && hasPriceDifference && <div className="mb-8" data-id="element-12404" data-mp="dlks8wcl2awm">
                <h3 className="text-[14px] font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-12405" data-mp="dmmg61ep6rvr">
                  Price Handling
                </h3>
                <div className="space-y-3" data-id="element-12406" data-mp="njqs78etx6yu">
                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-12407" data-mp="nipe0v2io4cw">
                    <input type="radio" name="priceHandling" value="keep-original" checked={priceHandling === 'keep-original'} onChange={e => setPriceHandling(e.target.value)} className="mt-1 w-5 h-5 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-12408" data-mp="23xh8ren4xnpd" />
                    <div data-id="element-12409" data-mp="26khybh6i150x">
                      <div className="text-[14px] font-medium text-[#0F172A]" data-id="element-12410" data-mp="2bhi0vi453pna">
                        Keep original price - Subscribers continue paying $
                        {originalProduct.price.toFixed(2)}
                      </div>
                      <div className="text-[13px] text-[#64748B] mt-1" data-id="element-12411" data-mp="21k3n5q9lpeyo">
                        Subscriber's price won't change. You absorb the
                        difference.
                      </div>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-12412" data-mp="2ggupa7ant4rz">
                    <input type="radio" name="priceHandling" value="use-new" checked={priceHandling === 'use-new'} onChange={e => setPriceHandling(e.target.value)} className="mt-1 w-5 h-5 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-12413" data-mp="2ges2dw4szrrh" />
                    <div data-id="element-12414" data-mp="3cyqyaay0lngp">
                      <div className="text-[14px] font-medium text-[#0F172A]" data-id="element-12415" data-mp="3d0238e37519e">
                        Use new product price - Subscribers will pay $
                        {replacementProduct.price.toFixed(2)}
                      </div>
                      <div className="text-[13px] text-[#64748B] mt-1" data-id="element-12416" data-mp="1fbzxyat8wruf">
                        Subscriber's price updates to match the new product.
                      </div>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-12417" data-mp="280x1t8wpmm3n">
                    <input type="radio" name="priceHandling" value="custom" checked={priceHandling === 'custom'} onChange={e => setPriceHandling(e.target.value)} className="mt-1 w-5 h-5 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-12418" data-mp="231u7oji9q0ag" />
                    <div className="flex-1" data-id="element-12419" data-mp="1u8h8iztm22fx">
                      <div className="text-[14px] font-medium text-[#0F172A] mb-2" data-id="element-12420" data-mp="2qr509dpgsodi">
                        Apply percentage adjustment - Custom pricing
                      </div>
                      {priceHandling === 'custom' && <div className="flex items-center gap-2 mb-2" data-id="element-12421" data-mp="8i24mgajkked">
                          <input type="number" value={customPercentage} onChange={e => setCustomPercentage(parseInt(e.target.value) || 0)} min="0" max="200" className="w-20 h-[32px] px-2 border border-[#CBD5E1] rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]" data-id="element-12422" data-mp="8jvee4ggbkql" />
                          <span className="text-[14px] text-[#64748B]" data-id="element-12423" data-mp="miux6vha95eu">
                            % of new price = $
                            {(replacementProduct.price * (customPercentage / 100)).toFixed(2)}
                          </span>
                        </div>}
                      <div className="text-[13px] text-[#64748B]" data-id="element-12424" data-mp="117r42p7lscdv">
                        e.g., 95% = $
                        {(replacementProduct.price * 0.95).toFixed(2)}
                      </div>
                    </div>
                  </label>
                </div>
              </div>}

            {/* Notification Options */}
            <div className="mb-8" data-id="element-12425" data-mp="1f3w9u9rr3eiq">
              <h3 className="text-[14px] font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-12426" data-mp="3b8u93817ml2c">
                Notification Options
              </h3>
              <div className="space-y-6" data-id="element-12427" data-mp="3687ndvvnw1s2">
                <div className="flex items-start justify-between" data-id="element-12428" data-mp="3kzgi2jef8dc2">
                  <div className="flex-1 pr-6" data-id="element-12429" data-mp="3l0zwe4gioeb7">
                    <h4 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-12430" data-mp="3g7f7d1wwiuo5">
                      Notify customers of the change
                    </h4>
                    <p className="text-[14px] text-[#64748B]" data-id="element-12431" data-mp="3pyjoa7650plm">
                      Send an email letting subscribers know their product has
                      been updated
                    </p>
                    {notifyCustomers && <button className="text-[14px] font-medium text-[#0EA5E9] hover:text-[#0284C7] mt-2" data-id="element-12432" data-mp="2tai6fo0heqg">
                        Preview email →
                      </button>}
                  </div>
                  <button onClick={() => setNotifyCustomers(!notifyCustomers)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${notifyCustomers ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-12433" data-mp="24lqdlbje3n1w">
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notifyCustomers ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-12434" data-mp="ac1puwr36707" />
                  </button>
                </div>

                {notifyCustomers && <div className="flex items-start justify-between pl-6 border-l-2 border-[#E2E8F0]" data-id="element-12435" data-mp="1ehn1jdks2nh">
                    <div className="flex-1 pr-6" data-id="element-12436" data-mp="69eu4krao1xq">
                      <h4 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-12437" data-mp="3nko9xw55gkrr">
                        Include reason in notification
                      </h4>
                      <p className="text-[14px] text-[#64748B] mb-3" data-id="element-12438" data-mp="1ls8dqy1y68f9">
                        Explain why the product is being replaced
                      </p>
                      {includeReason && <div data-id="element-12439" data-mp="1qoyvdptehc2s">
                          <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-12440" data-mp="eihjzvlz4485">
                            Reason for replacement
                          </label>
                          <textarea value={replacementReason} onChange={e => setReplacementReason(e.target.value)} placeholder="e.g., The 2022 vintage has sold out. We've upgraded you to our new 2023 vintage at no extra cost!" maxLength={500} rows={3} className="w-full px-3 py-2 border border-[#CBD5E1] rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent resize-none" data-id="element-12441" data-mp="4s6dq2ethiy3" />
                          <div className="text-[12px] text-[#94A3B8] mt-1 text-right" data-id="element-12442" data-mp="3hy6iuh3nuexe">
                            {replacementReason.length}/500
                          </div>
                        </div>}
                    </div>
                    <button onClick={() => setIncludeReason(!includeReason)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${includeReason ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-12443" data-mp="1g60ivoc3j5z1">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${includeReason ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-12444" data-mp="1np9or7le8mjc" />
                    </button>
                  </div>}
              </div>
            </div>

            {/* Timing Options */}
            <div data-id="element-12445" data-mp="zu6xyu314908">
              <h3 className="text-[14px] font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-12446" data-mp="zuq65j2vnj12">
                Timing Options
              </h3>
              <div className="space-y-3" data-id="element-12447" data-mp="19qtkdnb0784d">
                <label className="flex items-start gap-3 cursor-pointer" data-id="element-12448" data-mp="19prxmlkmkx6t">
                  <input type="radio" name="timing" value="immediate" checked={timing === 'immediate'} onChange={e => setTiming(e.target.value)} className="mt-1 w-5 h-5 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-12449" data-mp="1eqxqcjeackgc" />
                  <div data-id="element-12450" data-mp="2q3i41yz7vxjm">
                    <div className="text-[14px] font-medium text-[#0F172A]" data-id="element-12451" data-mp="3et4nb0rkg1ks">
                      Replace immediately
                    </div>
                    <div className="text-[13px] text-[#64748B] mt-1" data-id="element-12452" data-mp="3jr69hukagh0h">
                      Changes take effect now
                    </div>
                  </div>
                </label>

                <label className="flex items-start gap-3 cursor-pointer" data-id="element-12453" data-mp="3a1mpumor4j4k">
                  <input type="radio" name="timing" value="next-order" checked={timing === 'next-order'} onChange={e => setTiming(e.target.value)} className="mt-1 w-5 h-5 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-12454" data-mp="2w2wo2tqzlv9v" />
                  <div data-id="element-12455" data-mp="2n308dhmks6qy">
                    <div className="text-[14px] font-medium text-[#0F172A]" data-id="element-12456" data-mp="2n3jhc8u8zksu">
                      Replace on next order
                    </div>
                    <div className="text-[13px] text-[#64748B] mt-1" data-id="element-12457" data-mp="lav9tnakmb79">
                      Changes apply when next order processes
                    </div>
                  </div>
                </label>

                <label className="flex items-start gap-3 cursor-pointer" data-id="element-12458" data-mp="29tpxx5mvs95n">
                  <input type="radio" name="timing" value="scheduled" checked={timing === 'scheduled'} onChange={e => setTiming(e.target.value)} className="mt-1 w-5 h-5 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-12459" data-mp="29rvfbxz30t4q" />
                  <div className="flex-1" data-id="element-12460" data-mp="2essr9sti0qxs">
                    <div className="text-[14px] font-medium text-[#0F172A] mb-2" data-id="element-12461" data-mp="2erre9isx9udf">
                      Schedule replacement
                    </div>
                    {timing === 'scheduled' && <input type="date" value={scheduledDate} onChange={e => setScheduledDate(e.target.value)} className="w-48 h-[40px] px-3 border border-[#CBD5E1] rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]" data-id="element-12462" data-mp="2oi2o1lzaexs8" />}
                  </div>
                </label>

                <label className="flex items-start gap-3 cursor-pointer" data-id="element-12463" data-mp="185xa7sng96an">
                  <input type="radio" name="timing" value="threshold" checked={timing === 'threshold'} onChange={e => setTiming(e.target.value)} className="mt-1 w-5 h-5 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-12464" data-mp="136167ter5ev3" />
                  <div className="flex-1" data-id="element-12465" data-mp="3886lrkq19gvp">
                    <div className="text-[14px] font-medium text-[#0F172A]" data-id="element-12466" data-mp="3897yruqm0dg2">
                      Replace when stock reaches threshold
                    </div>
                    <div className="text-[13px] text-[#64748B] mt-1" data-id="element-12467" data-mp="3h8l88neqh20s">
                      Replacement runs automatically when inventory hits the
                      level you set
                    </div>

                    {timing === 'threshold' && <div className="mt-3 space-y-2" data-id="element-12468" data-mp="3r3wrnj136xev">
                        <div className="flex items-center gap-2" data-id="element-12469" data-mp="3vxr72a9nsq85">
                          <label className="text-[13px] text-[#475569]" data-id="element-12470" data-mp="4q58gw1srdy5">
                            Trigger at
                          </label>
                          <input type="number" min={0} max={999} value={thresholdUnits} onChange={e => setThresholdUnits(Number(e.target.value))} className="w-[80px] h-[40px] px-3 border border-[#CBD5E1] rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]" data-id="element-12471" data-mp="8tl1zjikky6m" />
                          <span className="text-[13px] text-[#475569]" data-id="element-12472" data-mp="1t8zkhg0u1lg6">
                            units remaining (net of reserved)
                          </span>
                        </div>
                        <div className="text-[13px] text-[#64748B]" data-id="element-12473" data-mp="1kae7s1i1l0nb">
                          Current stock: 47 units available
                        </div>
                        <div className="text-[13px] text-[#64748B]" data-id="element-12474" data-mp="2cy0a76fj9z8l">
                          Reserved for upcoming orders (next 7 days): 12 units
                        </div>
                        <div className="text-[13px] text-[#64748B] font-medium" data-id="element-12475" data-mp="285752hg5kovz">
                          Net available: 35 units
                        </div>
                        <div className="text-[13px] text-[#64748B] mt-3" data-id="element-12476" data-mp="30onvctegmld3">
                          At average sell-through, this trigger will fire in
                          approximately 9 days.
                        </div>

                        {thresholdUnits >= 5 && <div className="mt-3 p-4 bg-[#FEF3C7] border border-[#FDE68A] rounded-md flex items-start gap-3" data-id="element-12477" data-mp="2fjpvo04tsqm6">
                            <AlertTriangle className="w-5 h-5 text-[#92400E] flex-shrink-0 mt-0.5" data-id="element-12478" data-mp="26h891kl9dvyn" />
                            <div className="flex-1 space-y-3" data-id="element-12479" data-mp="2bf9sw7qidzb6">
                              <p className="text-[14px] text-[#92400E]" data-id="element-12480" data-mp="2bhmjf9ac1hzu">
                                Next 7 days of billing will consume 38 units,
                                leaving net available below your trigger
                                threshold of {thresholdUnits}.
                              </p>
                              <div data-id="element-12481" data-mp="2uedy15exj8xk">
                                <div className="text-[13px] font-semibold text-[#92400E] mb-2" data-id="element-12482" data-mp="2udcmz0xwymfu">
                                  When stock breaches the threshold:
                                </div>
                                <div className="space-y-2" data-id="element-12483" data-mp="3mvqaa6lmiq0o">
                                  <label className="flex items-start gap-2 cursor-pointer" data-id="element-12484" data-mp="44cu5c7r8l0">
                                    <input type="radio" name="breachBehaviour" value="wait-for-billing" checked={breachBehaviour === 'wait-for-billing'} onChange={e => setBreachBehaviour(e.target.value as 'wait-for-billing' | 'fire-immediately')} className="mt-1 w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-12485" data-mp="3s8exmjwdzrbp" />
                                    <span className="text-[13px] text-[#92400E]" data-id="element-12486" data-mp="3da4hkix5a2js">
                                      Wait for billing to complete, then fire
                                      (recommended)
                                    </span>
                                  </label>
                                  <label className="flex items-start gap-2 cursor-pointer" data-id="element-12487" data-mp="3i9pobfik3f72">
                                    <input type="radio" name="breachBehaviour" value="fire-immediately" checked={breachBehaviour === 'fire-immediately'} onChange={e => setBreachBehaviour(e.target.value as 'wait-for-billing' | 'fire-immediately')} className="mt-1 w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-12488" data-mp="12ibixc48k1w2" />
                                    <span className="text-[13px] text-[#92400E]" data-id="element-12489" data-mp="19kq6is9mgof7">
                                      Fire immediately, before the next billing
                                      cycle
                                    </span>
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>}
                      </div>}
                  </div>
                </label>
              </div>
            </div>
          </Card>}

        {/* Section 4: Affected Subscriptions */}
        {activeProduct && <Card data-id="element-12490" data-mp="10mo2e3uldfnp">
            <div className="mb-6" data-id="element-12491" data-mp="10n677ovbl278">
              <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-12492" data-mp="36qy3qisul78">
                Affected Subscriptions
              </h2>
              <p className="text-[14px] text-[#64748B] mt-1" data-id="element-12493" data-mp="83hcm9eyh8qj">
                {selectedCount} subscriptions will be updated
              </p>
            </div>

            {/* Summary */}
            <div className="p-4 bg-[#F8FAFC] rounded-md mb-6 space-y-2" data-id="element-12494" data-mp="3rt5gwqd7jghw">
              <div className="text-[14px] text-[#0F172A]" data-id="element-12495" data-mp="3ms7yccoackfv">
                <span className="font-semibold" data-id="element-12496" data-mp="3muu6adf391ym">
                  {activeProduct.subscriptionCount}
                </span>{' '}
                active subscriptions contain {activeProduct.name}
              </div>
              <div className="text-[14px] text-[#0F172A]" data-id="element-12497" data-mp="391cn4tqbdr94">
                Total quantity across subscriptions:{' '}
                <span className="font-semibold" data-id="element-12498" data-mp="341qaaleeed0u">47 bottles</span>
              </div>
              <div className="text-[14px] text-[#0F172A]" data-id="element-12499" data-mp="2z36piobjj7a2">
                Monthly revenue impact:{' '}
                <span className="font-semibold" data-id="element-12500" data-mp="2u3u2a2grby6y">
                  $
                  {operationType === 'sync' && selectedProduct ? ((selectedProduct.shopifyPrice - (selectedProduct.subscriptionPrice || selectedProduct.price)) * 47).toFixed(2) : (priceDifference * 47).toFixed(2)}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 mb-4" data-id="element-12501" data-mp="mqsp15m8zgld">
              <Button variant="ghost" size="sm" onClick={handleSelectAll} data-id="element-12502" data-mp="7ukx1j4llavj">
                Select All
              </Button>
              <Button variant="ghost" size="sm" onClick={handleDeselectAll} data-id="element-12503" data-mp="csurfrii07g3">
                Deselect All
              </Button>
            </div>

            {/* Table */}
            <div className="border border-[#E2E8F0] rounded-lg overflow-hidden mb-4" data-id="element-12504" data-mp="3v2l3cur8a4u">
              <table className="w-full" data-id="element-12505" data-mp="3lbaf44vt5weu">
                <thead className="bg-[#F1F5F9]" data-id="element-12506" data-mp="3lcc297q3m9cx">
                  <tr data-id="element-12507" data-mp="3q92ibv1wl4yc">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-12" data-id="element-12508" data-mp="3qadkjqfpa4nc">
                      Select
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-12509" data-mp="3gbo1vvfzcg61">
                      Subscription
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-12510" data-mp="2soaor008y4na">
                      Customer
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-12511" data-mp="36lpj8j5cx2m4">
                      Qty
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-12512" data-mp="3gkor3ziasu82">
                      Current Price
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-12513" data-mp="3pejrku8h5whd">
                      New Price
                    </th>
                    {operationType === 'sync' && <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-12514" data-mp="384wh2vq2x88">
                        Difference
                      </th>}
                    {operationType === 'replace' && <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-12515" data-mp="ap5mrvkzvw3t">
                        Next Order
                      </th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]" data-id="element-12516" data-mp="kmtrnecbejzp">
                  {affectedSubscriptions.slice(0, showAllSubscriptions ? undefined : 5).map(sub => {
                const newPrice = getNewPrice(sub.currentPrice);
                const difference = newPrice - sub.currentPrice;
                return <tr key={sub.id} className="hover:bg-[#F8FAFC] transition-colors" data-id="element-12517" data-mp="3j4fmwy5bqr8y">
                          <td className="px-4 py-3" data-id="element-12518" data-mp="1rd9sy2qx0jl">
                            <input type="checkbox" checked={sub.selected} onChange={() => handleToggleSubscription(sub.id)} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-12519" data-mp="3t4g68k3717d5" />
                          </td>
                          <td className="px-4 py-3" data-id="element-12520" data-mp="30fshufn5e1vd">
                            <button className="text-[14px] text-[#0EA5E9] hover:text-[#0284C7] font-medium" data-id="element-12521" data-mp="1fxhyalxqq9t5">
                              {sub.id}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-[14px] text-[#0F172A]" data-id="element-12522" data-mp="160vg64wstwut">
                            {sub.customer}
                          </td>
                          <td className="px-4 py-3 text-[14px] text-[#475569]" data-id="element-12523" data-mp="1awkl6nrl85yi">
                            {sub.quantity}
                          </td>
                          <td className="px-4 py-3 text-[14px] text-[#475569]" data-id="element-12524" data-mp="23m9lt00dy3zl">
                            ${sub.currentPrice.toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-[14px] font-semibold text-[#0F172A]" data-id="element-12525" data-mp="1tovf3s49ktkh">
                            ${newPrice.toFixed(2)}
                          </td>
                          {operationType === 'sync' && <td className="px-4 py-3" data-id="element-12526" data-mp="iahwp2sa56bu">
                              <span className="text-[14px] font-medium text-[#F59E0B]" data-id="element-12527" data-mp="i855dz0mtjm4">
                                +${difference.toFixed(2)}
                              </span>
                            </td>}
                          {operationType === 'replace' && <td className="px-4 py-3 text-[14px] text-[#475569]" data-id="element-12528" data-mp="2b6br0z6bb7s0">
                              {new Date(sub.nextOrder).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                            </td>}
                        </tr>;
              })}
                </tbody>
              </table>
            </div>

            {!showAllSubscriptions && affectedSubscriptions.length > 5 && <button onClick={() => setShowAllSubscriptions(true)} className="text-[14px] font-medium text-[#0EA5E9] hover:text-[#0284C7]" data-id="element-12529" data-mp="1n9y1x1d9uep6">
                View all {affectedSubscriptions.length} subscriptions →
              </button>}

            <p className="text-[13px] text-[#64748B] mt-4" data-id="element-12530" data-mp="3hnyw38xvmdix">
              Deselect any subscriptions you want to exclude from this{' '}
              {operationType === 'replace' ? 'replacement' : 'price sync'}.
            </p>
          </Card>}

        {/* Section 6: Recent Replacements */}
        <Card data-id="element-12531" data-mp="2rngt387ueku">
          <button onClick={() => setRecentReplacementsExpanded(!recentReplacementsExpanded)} className="w-full flex items-center justify-between" data-id="element-12532" data-mp="2t6y8x5lyvo7">
            <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-12533" data-mp="3763s9a7a3qw1">
              Recent{' '}
              {operationType === 'replace' ? 'Replacements' : 'Price Syncs'}
            </h2>
            {recentReplacementsExpanded ? <ChevronUp className="w-5 h-5 text-[#64748B]" data-id="element-12534" data-mp="3g58pmd73te6m" /> : <ChevronDown className="w-5 h-5 text-[#64748B]" data-id="element-12535" data-mp="3l53hok2m89t9" />}
          </button>

          {recentReplacementsExpanded && <div className="mt-6" data-id="element-12536" data-mp="vg1dnfnkj4ut">
              <div className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-id="element-12537" data-mp="veztmlokr80y">
                <table className="w-full" data-id="element-12538" data-mp="10dt7if7rx11n">
                  <thead className="bg-[#F1F5F9]" data-id="element-12539" data-mp="10gyvfr4o1wvs">
                    <tr data-id="element-12540" data-mp="29b17gyf3d5mg">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-12541" data-mp="29cci9i6yfdn2">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-12542" data-mp="2idk4h1w74o0s">
                        Original
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-12543" data-mp="2sa59hn87tz4m">
                        Replacement
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-12544" data-mp="312qs93buk5tg">
                        Subscriptions
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-12545" data-mp="2x3rgayrp78si">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E2E8F0]" data-id="element-12546" data-mp="3pnc1bsje15vu">
                    {[{
                  date: 'Jan 5, 2026',
                  original: 'Chardonnay 2021',
                  replacement: 'Chardonnay 2022',
                  count: 15
                }, {
                  date: 'Dec 28, 2025',
                  original: 'Pinot Noir 750ml',
                  replacement: 'Pinot Noir 750ml (New Label)',
                  count: 8
                }, {
                  date: 'Dec 15, 2025',
                  original: 'Holiday Gift Box',
                  replacement: 'Winter Collection Box',
                  count: 31
                }].map((item, index) => <tr key={index} className="hover:bg-[#F8FAFC] transition-colors" data-id="element-12547" data-mp="2wxd439v1i2g3" data-mpi={index}>
                        <td className="px-4 py-3 text-[14px] text-[#475569]" data-id="element-12548" data-mp="31w6kpb5mi9kh">
                          {item.date}
                        </td>
                        <td className="px-4 py-3 text-[14px] text-[#0F172A]" data-id="element-12549" data-mp="2s0mt30y3dx1j">
                          {item.original}
                        </td>
                        <td className="px-4 py-3 text-[14px] text-[#0F172A]" data-id="element-12550" data-mp="yip7qkc1mx5q">
                          {item.replacement}
                        </td>
                        <td className="px-4 py-3 text-[14px] text-[#475569]" data-id="element-12551" data-mp="oqt64zar0owr">
                          {item.count}
                        </td>
                        <td className="px-4 py-3" data-id="element-12552" data-mp="jub1dzff4qkf">
                          <div className="flex items-center gap-2 text-[14px] text-[#059669]" data-id="element-12553" data-mp="1hgi8ph33wx0h">
                            <CheckCircle className="w-4 h-4" data-id="element-12554" data-mp="1cgneb3k4hpao" />
                            <span data-id="element-12555" data-mp="1cfcetfxpn3pd">Complete</span>
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
              <button onClick={() => navigate('/operations/activity-log')} className="text-[14px] font-medium text-[#0EA5E9] hover:text-[#0284C7] mt-4" data-id="element-12556" data-mp="13itli2lkhds9">
                View all in Activity Log →
              </button>
            </div>}
        </Card>
      </div>

      {/* Sticky Footer */}
      {(operationType === 'replace' && originalProduct && replacementProduct || operationType === 'sync' && selectedProduct) && <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E2E8F0] shadow-lg z-30" data-id="element-12557" data-mp="2cpc2zyrbu31g">
          <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-12558" data-mp="2hfsgtjz27qoc">
            <Button variant="outline" onClick={() => {
          setOriginalProduct(null);
          setReplacementProduct(null);
          setSelectedProduct(null);
        }} data-id="element-12559" data-mp="2mf5cmu7ws832">
              Cancel
            </Button>
            <div className="flex items-center gap-3" data-id="element-12560" data-mp="2vfbpqezp361p">
              <Button variant="outline" onClick={() => setShowConfirmModal(true)} data-id="element-12561" data-mp="2vdqrofe694z3">
                Preview Changes
              </Button>
              <Button variant="primary" onClick={handleExecute} disabled={selectedCount === 0} data-id="element-12562" data-mp="112c0du5iixh8">
                {operationType === 'replace' ? `Execute Replace (${selectedCount})` : `Sync Prices (${selectedCount})`}
              </Button>
            </div>
          </div>
        </div>}

      {/* Confirmation Modal */}
      {showConfirmModal && (operationType === 'replace' && originalProduct && replacementProduct || operationType === 'sync' && selectedProduct) && <>
            <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setShowConfirmModal(false)} data-id="element-12563" data-mp="3hq38hn6k2115" />
            <div className="fixed inset-0 flex items-center justify-center z-50 p-6" data-id="element-12564" data-mp="2ozbf0xgvjqhi">
              <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-id="element-12565" data-mp="2kxzo8om9kz7q">
                {/* Header */}
                <div className="p-6 border-b border-[#E2E8F0] flex items-center justify-between" data-id="element-12566" data-mp="2yysjjbke3ybw">
                  <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-12567" data-mp="2txmq1biwo71b">
                    Confirm{' '}
                    {operationType === 'replace' ? 'Product Replacement' : 'Price Sync'}
                  </h2>
                  <button onClick={() => setShowConfirmModal(false)} className="p-2 hover:bg-[#F1F5F9] rounded-md transition-colors" data-id="element-12568" data-mp="97xbkzm46n95">
                    <X className="w-5 h-5 text-[#64748B]" data-id="element-12569" data-mp="1w5589s2r67r2" />
                  </button>
                </div>

                {/* Body */}
                <div className="p-6 space-y-6" data-id="element-12570" data-mp="1i65gibjy1cqo">
                  {/* Summary */}
                  <div className="space-y-4" data-id="element-12571" data-mp="1n3ynh6t2mas0">
                    {operationType === 'replace' && originalProduct && replacementProduct ? <div className="flex items-center gap-4" data-id="element-12572" data-mp="2av43blz0h32b">
                        <div className="flex-1 p-3 bg-[#F8FAFC] rounded-md" data-id="element-12573" data-mp="2evmv5g0wm94i">
                          <div className="text-[12px] text-[#94A3B8] mb-1" data-id="element-12574" data-mp="2exr3ca5u5ual">
                            Original
                          </div>
                          <div className="flex items-center gap-2" data-id="element-12575" data-mp="262jb2ovr6y0w">
                            <span className="text-2xl" data-id="element-12576" data-mp="2tsx8yw10rr3e">
                              {originalProduct.image}
                            </span>
                            <span className="text-[14px] font-medium text-[#0F172A]" data-id="element-12577" data-mp="2ykf8rgrb3lms">
                              {originalProduct.name}
                            </span>
                          </div>
                        </div>
                        <ArrowRight className="w-6 h-6 text-[#94A3B8]" data-id="element-12578" data-mp="9b8ppfwzd20w" />
                        <div className="flex-1 p-3 bg-[#F8FAFC] rounded-md" data-id="element-12579" data-mp="e5d2ifsxg49w">
                          <div className="text-[12px] text-[#94A3B8] mb-1" data-id="element-12580" data-mp="i5wz9i9l95vi">
                            Replacement
                          </div>
                          <div className="flex items-center gap-2" data-id="element-12581" data-mp="3hmv9kl0vl799">
                            <span className="text-2xl" data-id="element-12582" data-mp="3mkocn56vtl5b">
                              {replacementProduct.image}
                            </span>
                            <span className="text-[14px] font-medium text-[#0F172A]" data-id="element-12583" data-mp="3vkjsvofao6c7">
                              {replacementProduct.name}
                            </span>
                          </div>
                        </div>
                      </div> : selectedProduct && <div className="p-4 bg-[#F8FAFC] rounded-md" data-id="element-12584" data-mp="1ashmq7my0xts">
                          <div className="text-[12px] text-[#94A3B8] mb-2" data-id="element-12585" data-mp="1kx7s6nhk2k2r">
                            Product
                          </div>
                          <div className="flex items-center gap-2" data-id="element-12586" data-mp="1g198veb33s97">
                            <span className="text-2xl" data-id="element-12587" data-mp="1b750qrq31e6j">
                              {selectedProduct.image}
                            </span>
                            <span className="text-[14px] font-medium text-[#0F172A]" data-id="element-12588" data-mp="127rq3vq82jk8">
                              {selectedProduct.name}
                            </span>
                          </div>
                          <div className="mt-2 text-[14px] text-[#64748B]" data-id="element-12589" data-mp="s9ln1asoj7ds">
                            Price: $
                            {(selectedProduct.subscriptionPrice || selectedProduct.price).toFixed(2)}{' '}
                            → ${selectedProduct.shopifyPrice.toFixed(2)}
                          </div>
                        </div>}

                    <div className="grid grid-cols-2 gap-4" data-id="element-12590" data-mp="2imj9jj5e4ghe">
                      <div className="p-3 bg-[#F8FAFC] rounded-md" data-id="element-12591" data-mp="fyue8xxstg5v">
                        <div className="text-[12px] text-[#94A3B8] mb-1" data-id="element-12592" data-mp="fxja2x0fy6e8">
                          Subscriptions affected
                        </div>
                        <div className="text-[20px] font-semibold text-[#0F172A]" data-id="element-12593" data-mp="6zpl6750af13">
                          {selectedCount}
                        </div>
                      </div>
                      <div className="p-3 bg-[#F8FAFC] rounded-md" data-id="element-12594" data-mp="yplw6nvuqevv">
                        <div className="text-[12px] text-[#94A3B8] mb-1" data-id="element-12595" data-mp="13oyhv5azlfwv">
                          Price change
                        </div>
                        <div className="text-[20px] font-semibold text-[#0F172A]" data-id="element-12596" data-mp="po49unrupgyq">
                          {operationType === 'sync' && selectedProduct ? `$${(selectedProduct.shopifyPrice - (selectedProduct.subscriptionPrice || selectedProduct.price)).toFixed(2)} increase` : hasPriceDifference ? `$${Math.abs(priceDifference).toFixed(2)} ${priceDifference > 0 ? 'increase' : 'decrease'}` : 'None'}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 text-[14px]" data-id="element-12597" data-mp="32wnb687vtrf0">
                      <div className="flex justify-between" data-id="element-12598" data-mp="32y85bwqab8cc">
                        <span className="text-[#64748B]" data-id="element-12599" data-mp="3gubz5ox8xeoa">
                          Customer notification:
                        </span>
                        <span className="font-medium text-[#0F172A]" data-id="element-12600" data-mp="3buqyna2fgy9g">
                          {notifyCustomers ? 'Yes' : 'No'}
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-12601" data-mp="2j5tgd6y7v1gp">
                        <span className="text-[#64748B]" data-id="element-12602" data-mp="2xxu6n5akpaqz">Timing:</span>
                        <span className="font-medium text-[#0F172A]" data-id="element-12603" data-mp="2t6c4idwtd44f">
                          {timing === 'immediate' && 'Immediately'}
                          {timing === 'next-order' && 'On next order'}
                          {timing === 'scheduled' && `Scheduled for ${scheduledDate}`}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Warning */}
                  <div className="p-4 bg-[#FEE2E2] border border-[#FCA5A5] rounded-md flex items-start gap-3" data-id="element-12604" data-mp="18pw9phiij4hw">
                    <AlertTriangle className="w-5 h-5 text-[#B91C1C] flex-shrink-0 mt-0.5" data-id="element-12605" data-mp="1cp4z6a6isg7n" />
                    <div data-id="element-12606" data-mp="1hizivdm4kv6q">
                      <p className="text-[14px] font-semibold text-[#B91C1C] mb-1" data-id="element-12607" data-mp="1hj9af5mtjaej">
                        3 subscriptions have orders processing within 24 hours
                      </p>
                      <p className="text-[13px] text-[#B91C1C]" data-id="element-12608" data-mp="1rhp1x9prclok">
                        These will use the new{' '}
                        {operationType === 'replace' ? 'product' : 'price'}.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-[#E2E8F0] flex items-center justify-end gap-3" data-id="element-12609" data-mp="629joeg4d3mm">
                  <Button variant="outline" onClick={() => setShowConfirmModal(false)} data-id="element-12610" data-mp="3uo4k5wgyt8iq">
                    Cancel
                  </Button>
                  <Button variant="primary" onClick={handleConfirmExecute} data-id="element-12611" data-mp="3pwczg2khpx0i">
                    Confirm & Execute
                  </Button>
                </div>
              </div>
            </div>
          </>}

      {/* Threshold Already Breached Modal */}
      {showThresholdBreachedModal && originalProduct && <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setShowThresholdBreachedModal(false)} data-id="element-12612" data-mp="hh2u8llb0boi" />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-6" data-id="element-12613" data-mp="cgovju5lee35">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" data-id="element-12614" data-mp="8f3d7taah7lk">
              {/* Header */}
              <div className="p-6 border-b border-[#E2E8F0] flex items-center justify-between" data-id="element-12615" data-mp="2t6vphbeh5ot0">
                <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-12616" data-mp="2o8c174ces0xh">
                  Stock is already below the threshold
                </h2>
                <button onClick={() => setShowThresholdBreachedModal(false)} className="p-2 hover:bg-[#F1F5F9] rounded-md transition-colors" data-id="element-12617" data-mp="2ppr6m814e6md">
                  <X className="w-5 h-5 text-[#64748B]" data-id="element-12618" data-mp="2gojjmm420s7l" />
                </button>
              </div>

              {/* Body */}
              <div className="p-6 space-y-4" data-id="element-12619" data-mp="2kpd7jfhhy70h">
                <div className="flex items-start gap-3" data-id="element-12620" data-mp="2ks7qf2kv3jrs">
                  <AlertTriangle className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" data-id="element-12621" data-mp="26xovfc78ziji" />
                  <p className="text-[14px] text-[#0F172A] leading-relaxed" data-id="element-12622" data-mp="2bpq2mjpk6j2o">
                    {originalProduct.name} has {originalProduct.stock} units
                    available, with 12 reserved for upcoming orders in the next
                    7 days — leaving net available below your trigger threshold
                    of {thresholdUnits} units.
                  </p>
                </div>
                <p className="text-[14px] text-[#64748B] leading-relaxed" data-id="element-12623" data-mp="1sxxb6tq515x2">
                  Because the threshold is already breached, this replacement
                  will run immediately if you proceed. The trigger won't wait —
                  it fires now.
                </p>
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-[#E2E8F0] flex items-center justify-end gap-3" data-id="element-12624" data-mp="1if6epdoc0np0">
                <Button variant="outline" onClick={() => setShowThresholdBreachedModal(false)} data-id="element-12625" data-mp="1eayn98qhotn3">
                  Cancel and reconsider
                </Button>
                <Button variant="primary" onClick={() => {
              setShowThresholdBreachedModal(false);
              handleConfirmExecute();
            }} data-id="element-12626" data-mp="1ndpyb7fgdne7">
                  Run replacement now
                </Button>
              </div>
            </div>
          </div>
        </>}

      {/* Processing Overlay */}
      {isProcessing && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center" data-id="element-12627" data-mp="2b2sjgp9apydg">
          <div className="bg-white rounded-lg p-8 flex flex-col items-center" data-id="element-12628" data-mp="261cuwn7wt9qb">
            <RefreshCw className="w-12 h-12 text-[#0EA5E9] animate-spin mb-4" data-id="element-12629" data-mp="3dfdccm8pp09o" />
            <p className="text-[18px] font-semibold text-[#0F172A]" data-id="element-12630" data-mp="3idx10ueowq5q">
              Processing{' '}
              {operationType === 'replace' ? 'replacement' : 'price sync'}...
            </p>
          </div>
        </div>}
    </div>;
}